import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repo } from '../data/repo';
import { balanceAsOf } from '../data/types';
import { errorMessage } from '../data/repo';
import { exitDemo, enterDemo } from '../data/mode';
import { AppContext, type AppCtx, type Base } from './context';
import { useRoute } from './hooks';
import { ErrorNote, Loading } from './ui';
import { SignIn } from './SignIn';
import { ShellSummary } from './ShellSummary';
import { QuickCapture } from './QuickCapture';
import { Report } from './screens/Report';
import { Home } from './screens/Home';
import { Spend, periodState } from './screens/Spend';
import { Wealth } from './screens/Wealth';
import { Insights } from './screens/Insights';
import { Close } from './screens/Close';
import { SECTIONS, navigate, type Route, type Section } from '../lib/route';
import { closeTargetMonth, currentMonthStart, latestPublished, monthKey } from '../lib/periods';
import { dateLabel, monthLabel } from '../lib/format';

export const BUILD_ID: string = import.meta.env.VITE_BUILD_ID ?? 'dev';

/** Which database this build talks to, so staging can never be mistaken for production. */
const DB_REF = /^https:\/\/([a-z0-9]+)\.supabase\.co/.exec(import.meta.env.VITE_SUPABASE_URL ?? '')?.[1] ?? null;
const DB_LABEL: Record<string, string> = { khyolhuzfngwskdrkilt: 'Production', wurujhipoldyhjigjggj: 'Staging' };
export const DATABASE: string = DB_REF ? `${DB_LABEL[DB_REF] ?? 'Database'} (${DB_REF.slice(0, 6)})` : 'none';

const LABEL: Record<Section, string> = { home: 'Home', spend: 'Spend', wealth: 'Wealth', insights: 'Insights' };
const svg = (d: string) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>
);
const ICON: Record<Section, JSX.Element> = {
  home: svg('M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z'),
  spend: svg('M6 3h12v18l-3-2-3 2-3-2-3 2zM9 8h6M9 12h6M9 16h3'),
  wealth: svg('M4 20h16M6 20V11M10 20V7M14 20v-9M18 20V4'),
  insights: svg('M12 3v4M12 17v4M3 12h4M17 12h4M6.3 6.3l2.8 2.8M14.9 14.9l2.8 2.8M6.3 17.7l2.8-2.8M14.9 9.1l2.8-2.8'),
};
const EMPTY_BASE: Base = { months: [], categories: [], accounts: [], positions: [], openPlan: null };

/** Optional data that must never blank the app if it fails (e.g. a table missing). */
async function soft<T>(p: Promise<T[]>, label: string, errors: string[]): Promise<T[]> {
  try { return await p; } catch (e) { errors.push(`${label}: ${errorMessage(e)}`); return []; }
}

export function App({ repo }: { repo: Repo }) {
  const [auth, setAuth] = useState<'checking' | 'out' | 'in'>('checking');
  const [base, setBase] = useState<Base | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [capture, setCapture] = useState(false);
  const route = useRoute();

  useEffect(() => {
    const check = () => repo.currentUserId().then((id) => setAuth(id ? 'in' : 'out'));
    void check();
    return repo.onAuthChange(() => void check());
  }, [repo]);

  const load = useCallback(async () => {
    const errors: string[] = [];
    let months = await repo.months();
    // The current month always exists as an Open Plan carrying targets only, never actuals.
    const open = currentMonthStart();
    let openPlan = months.find((m) => m.month_start === open) ?? null;
    if (!openPlan && !repo.isDemo) {
      const template = latestPublished(months) ?? months[0] ?? null;
      try {
        openPlan = await repo.ensureMonth(open, template);
        months = await repo.months();
      } catch (e) { errors.push(`Open plan: ${errorMessage(e)}`); }
    }
    const [categories, accounts, positions] = await Promise.all([
      soft(repo.categories(), 'Categories', errors),
      soft(repo.accounts(), 'Accounts', errors),
      soft(repo.capitalPositions(), 'Capital positions', errors),
    ]);
    setBaseErrors(errors);
    setBase({ months, categories, accounts, positions, openPlan });
  }, [repo]);

  useEffect(() => {
    if (auth !== 'in') { setBase(null); return; }
    setFatal(null);
    load().catch((e) => setFatal(errorMessage(e)));
  }, [auth, load]);

  const ctx: AppCtx | null = useMemo(() => (base ? { repo, base, baseErrors, refresh: load } : null), [repo, base, baseErrors, load]);

  if (auth === 'checking') return <main className="gate"><Loading label="Checking sign-in…" /></main>;
  if (auth === 'out') return <SignIn repo={repo} />;
  if (fatal) return <main className="gate"><ErrorNote message={fatal} onRetry={() => { setFatal(null); void load().catch((e) => setFatal(errorMessage(e))); }} /></main>;
  if (!ctx) return <main className="gate"><Loading label="Loading your months…" /></main>;

  const active: Section = route.section === 'close' ? 'home' : route.section === 'report' ? 'insights' : route.section;
  const target = closeTargetMonth(ctx.base.months);

  return (
    <AppContext.Provider value={ctx}>
      <ShellSummary />
      {repo.isDemo && <div className="demo-flag">DEMO · Synthetic data</div>}
      <div className={`shell ${repo.isDemo ? 'is-demo' : ''}`}>
        <nav className="nav" aria-label="Primary">
          <div className="brand">Finance<strong>Cockpit</strong></div>
          {SECTIONS.map((s) => (
            <a key={s} href={`#/${s}`} className="nav-item" aria-current={s === active ? 'page' : undefined}>
              {ICON[s]}<span>{LABEL[s]}</span>
            </a>
          ))}
        </nav>
        <div className="main">
          <header className="top">
            <div className="top-context">{contextLine(route, ctx.base)}</div>
            <div className="top-actions">
              <button className="secondary" aria-label="Quick Capture" onClick={() => setCapture(true)}>+<span className="capture-label"> Capture</span></button>
              {target && route.section !== 'close' && <button className="cta" aria-label={`Close ${monthLabel(target)}`} onClick={() => navigate({ section: 'close' })}>Close {new Date(`${target}T00:00:00`).toLocaleDateString('en-GB', { month: 'short' })}</button>}
              {repo.isDemo
                ? <button className="secondary" onClick={exitDemo}>Exit demo</button>
                : <details className="menu">
                    <summary aria-label="Account menu">⋯</summary>
                    <div>
                      <button onClick={() => void enterDemo(() => repo.signOut())}>Demo Mode</button>
                      <button onClick={() => void repo.signOut()}>Sign out</button>
                    </div>
                  </details>}
            </div>
          </header>
          {baseErrors.length > 0 && <ErrorNote message={baseErrors.join(' · ')} onRetry={() => void load()} />}
          <main className="content">{screen(route)}</main>
          <footer className="build">Build {BUILD_ID} · {repo.isDemo ? 'Demo data' : DATABASE}</footer>
        </div>
      </div>
      {capture && <QuickCapture onClose={() => setCapture(false)} />}
    </AppContext.Provider>
  );
}

function screen(route: Route) {
  switch (route.section) {
    case 'spend': return <Spend month={route.month} category={route.category} />;
    case 'wealth': return <Wealth />;
    case 'insights': return <Insights month={route.month} question={route.question} />;
    case 'close': return <Close />;
    case 'report': return <Report month={route.month} />;
    default: return <Home />;
  }
}

/** The header always states the period on screen and whether it is plan, review, actuals or as-of. */
const STATE_WORD = { 'Published Actuals': 'sealed', 'Open Plan': 'open plan', Review: 'in review', 'As of': 'as of' } as const;

function contextLine(route: Route, base: Base): string {
  const latest = latestPublished(base.months);
  switch (route.section) {
    case 'spend': {
      const m = (route.month && base.months.find((x) => monthKey(x.month_start) === route.month)) || latest || base.openPlan;
      return m ? `${monthLabel(m.month_start)}, ${STATE_WORD[periodState(m, base.openPlan?.month_start)]}` : 'Spend';
    }
    case 'insights': {
      const m = (route.month && base.months.find((x) => monthKey(x.month_start) === route.month && x.status === 'published')) || latest;
      return m ? `${monthLabel(m.month_start)}, sealed` : 'Nothing sealed yet';
    }
    case 'wealth': {
      const dates = [...base.accounts.map(balanceAsOf), ...base.positions.map((p) => p.updated_at)].sort();
      if (!dates.length) return 'Wealth';
      const first = dateLabel(dates[0]);
      const last = dateLabel(dates[dates.length - 1]);
      return first === last ? `Recorded ${first}` : `Recorded ${first} to ${last}`;
    }
    case 'report':
      return route.month ? `${monthLabel(`${route.month}-01`)}, close pack` : 'Close pack';
    case 'close': {
      const t = closeTargetMonth(base.months);
      return t ? `Closing ${monthLabel(t)}` : 'Close Month';
    }
    default:
      return base.openPlan ? `${monthLabel(base.openPlan.month_start)}, open plan` : 'Home';
  }
}
